import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Camera, Gauge, Fuel } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InspectionReport() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    axios.get(`${API}/inspection-report/${token}`)
      .then(({ data }) => setReport(data))
      .catch(() => setInvalid(true));
  }, [token]);

  const confirm = async () => {
    if (!name.trim()) { toast.error("Please type your full name to confirm."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/inspection-report/${token}/signoff`, { name: name.trim() });
      setReport((r) => ({ ...r, signoff: data.signoff }));
      toast.success("Thank you — vehicle condition confirmed!");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record confirmation — please try again."); }
    finally { setBusy(false); }
  };

  if (invalid) return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <p className="text-white/60 text-sm" data-testid="inspection-report-invalid">This report link is not valid. Please contact Auto-Hire STL.</p>
    </div>
  );
  if (!report) return (
    <div className="min-h-screen bg-black flex items-center justify-center" data-testid="inspection-report-loading">
      <Loader2 className="animate-spin text-[#F2B705]" size={28} />
    </div>
  );

  return (
    <div className="min-h-screen bg-black px-4 py-10">
      <div className="max-w-lg mx-auto" data-testid="inspection-report-page">
        <p className="text-[#F2B705] font-bold tracking-widest text-xs uppercase">Auto-Hire STL</p>
        <h1 className="font-display font-black text-2xl text-white mt-1">{report.label} Condition Report</h1>
        <p className="text-white/60 text-sm mt-2">
          Hi {report.name.split(" ")[0] || "there"}, please review the condition of your <b className="text-white">{report.vehicle}</b> below.
        </p>

        <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.03] p-4 grid grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-1"><Gauge size={11} /> Mileage</p>
            <p className="text-white font-semibold mt-0.5" data-testid="report-mileage">{report.mileage || "—"}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-1"><Fuel size={11} /> Fuel</p>
            <p className="text-white font-semibold mt-0.5" data-testid="report-fuel">{report.fuel_level || "—"}</p>
          </div>
          {report.notes && (
            <div className="col-span-2">
              <p className="text-[10px] uppercase tracking-widest text-white/40">Notes / Condition</p>
              <p className="text-white/80 text-sm mt-0.5" data-testid="report-notes">{report.notes}</p>
            </div>
          )}
        </div>

        <p className="text-[10px] uppercase tracking-widest text-white/40 mt-6 mb-2 flex items-center gap-1">
          <Camera size={11} /> {report.photos.length} Photo{report.photos.length === 1 ? "" : "s"} — tap to enlarge
        </p>
        <div className="grid grid-cols-3 gap-2">
          {report.photos.map((p, i) => (
            <button key={i} onClick={() => setViewer(i)} className="relative rounded-lg overflow-hidden border border-white/10" data-testid={`report-photo-${i}`}>
              <img src={`${API}/inspection-report/${token}/photo/${i}`} alt={p.tag} loading="lazy" className="w-full h-24 object-cover" />
              <span className="absolute bottom-0 left-0 right-0 text-[10px] bg-black/70 text-white/80 px-1 py-0.5">{p.tag}</span>
            </button>
          ))}
        </div>

        {report.signoff ? (
          <div className="mt-8 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-5 text-center" data-testid="report-signed-badge">
            <CheckCircle2 className="mx-auto text-emerald-400" size={28} />
            <p className="text-emerald-300 font-bold mt-2">Condition Confirmed</p>
            <p className="text-white/60 text-xs mt-1">
              Confirmed by {report.signoff.name} on {new Date(report.signoff.at).toLocaleString()}
            </p>
          </div>
        ) : (
          <div className="mt-8 rounded-xl border border-[#F2B705]/30 bg-[#F2B705]/5 p-5">
            <p className="text-white font-bold text-sm">Confirm Vehicle Condition</p>
            <p className="text-white/60 text-xs mt-1">
              By confirming, you agree the photos and notes above accurately show the vehicle's condition at {report.type === "pre" ? "pickup" : "return"}.
              If anything looks wrong, contact Auto-Hire STL <b>before</b> confirming.
            </p>
            <input data-testid="signoff-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Type your full legal name"
              className="mt-3 w-full px-3 py-2.5 rounded-lg bg-black border border-white/15 text-white text-sm outline-none focus:border-[#F2B705]" />
            <button data-testid="signoff-confirm-btn" onClick={confirm} disabled={busy}
              className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#F2B705] text-black font-bold disabled:opacity-60">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />} I Agree — Confirm Condition
            </button>
          </div>
        )}

        <p className="text-white/25 text-[11px] text-center mt-8">Ride With Us · Auto-Hire STL · St. Louis, MO</p>
      </div>

      {viewer !== null && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-3" onClick={() => setViewer(null)} data-testid="report-photo-viewer">
          <img src={`${API}/inspection-report/${token}/photo/${viewer}`} alt="" className="max-w-full max-h-full rounded-lg" />
          <button className="absolute top-4 right-4 text-white/70 text-2xl leading-none">✕</button>
        </div>
      )}
    </div>
  );
}
